import '../scss/main.scss';
import initPropertyGallery from './property-gallery';
import initPropertyHeroGallery from './property-hero-gallery';
import initPropertyHeroSeasons from './property-hero-seasons';

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

function initDateSearchForms() {
  const forms = document.querySelectorAll('[data-hero-search], .catalog-search__form');
  if (!forms.length) return;

  const today = new Date();
  const max = addDays(new Date(today.getFullYear(), today.getMonth() + 24, today.getDate()), 0);

  forms.forEach((form) => {
    const arrival = form.querySelector('input[name="arrival"]');
    const departure = form.querySelector('input[name="departure"]');
    const guests = form.querySelector('select[name="guests"]');

    if (arrival) {
      arrival.min = formatDateInput(today);
      arrival.max = formatDateInput(max);
      if (!arrival.value) {
        arrival.value = formatDateInput(today);
      }
    }

    if (departure) {
      departure.min = formatDateInput(addDays(today, 1));
      departure.max = formatDateInput(max);
      if (!departure.value) {
        departure.value = formatDateInput(addDays(today, 5));
      }
    }

    if (guests && !guests.value) {
      guests.value = '2';
    }

    if (arrival && departure) {
      arrival.addEventListener('change', () => {
        const arrivalDate = new Date(arrival.value);
        if (!Number.isNaN(arrivalDate.getTime())) {
          const nextDay = addDays(arrivalDate, 1);
          departure.min = formatDateInput(nextDay);
          if (new Date(departure.value) <= arrivalDate) {
            departure.value = formatDateInput(addDays(arrivalDate, 4));
          }
        }
      });
    }

    // Make full date field open picker when supported
    [arrival, departure].forEach((input) => {
      if (!input || typeof input.showPicker !== 'function') return;
      ['click', 'focus'].forEach((evt) => {
        input.addEventListener(evt, () => {
          try {
            input.showPicker();
          } catch (_error) {
            /* no-op */
          }
        });
      });
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const yearTarget = document.querySelector('[data-current-year]');

  if (yearTarget) {
    yearTarget.textContent = new Date().getFullYear().toString();
  }

  const navToggle = document.querySelector('[data-nav-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');

  if (navToggle && mobileNav) {
    const closeMobileNav = () => {
      navToggle.setAttribute('aria-expanded', 'false');
      mobileNav.classList.remove('is-open');
      mobileNav.setAttribute('hidden', '');
    };

    const openMobileNav = () => {
      navToggle.setAttribute('aria-expanded', 'true');
      mobileNav.classList.add('is-open');
      mobileNav.removeAttribute('hidden');
    };

    navToggle.addEventListener('click', () => {
      const isExpanded = navToggle.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        closeMobileNav();
      } else {
        openMobileNav();
      }
    });

    mobileNav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', closeMobileNav);
    });
  }

  const dropdown = document.querySelector('.site-nav__dropdown');
  if (dropdown) {
    const toggle = dropdown.querySelector('.site-nav__dropdown-toggle');

    if (toggle) {
      const setExpanded = (state) => {
        toggle.setAttribute('aria-expanded', state ? 'true' : 'false');
      };

      dropdown.addEventListener('mouseenter', () => setExpanded(true));
      dropdown.addEventListener('mouseleave', () => setExpanded(false));
      toggle.addEventListener('focus', () => setExpanded(true));

      dropdown.addEventListener('focusout', (event) => {
        if (event.relatedTarget && dropdown.contains(event.relatedTarget)) {
          return;
        }
        setExpanded(false);
      });
    }
  }

  const testimonialSliders = document.querySelectorAll('[data-testimonial-slider]');

  testimonialSliders.forEach((slider) => {
    const slides = Array.from(slider.querySelectorAll('[data-testimonial-slide]'));
    if (slides.length <= 1) {
      return;
    }

    const dots = Array.from(slider.querySelectorAll('[data-testimonial-dot]'));
    let activeIndex = 0;
    let timerId;
    const interval = 5000;

    const setActiveSlide = (nextIndex) => {
      slides.forEach((slide, index) => {
        const isActive = index === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      dots.forEach((dot, index) => {
        const isActive = index === nextIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      activeIndex = nextIndex;
    };

    const startTimer = () => {
      stopTimer();
      timerId = window.setInterval(() => {
        const nextIndex = (activeIndex + 1) % slides.length;
        setActiveSlide(nextIndex);
      }, interval);
    };

    const stopTimer = () => {
      if (timerId) {
        window.clearInterval(timerId);
        timerId = undefined;
      }
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        setActiveSlide(index);
        startTimer();
      });
    });

    slider.addEventListener('pointerenter', stopTimer);
    slider.addEventListener('pointerleave', startTimer);

    setActiveSlide(0);
    startTimer();
  });

  const openButtons = document.querySelectorAll('[data-modal-open]');
  const modals = document.querySelectorAll('[data-modal]');
  const body = document.body;

  const closeModal = (modal) => {
    modal.classList.remove('is-open');
    modal.setAttribute('hidden', '');
    body.classList.remove('has-modal');
    resetModalForm(modal);
  };

  function resetModalForm(modal) {
    const form = modal.querySelector('[data-modal-form]');
    const status = modal.querySelector('[data-modal-status]');
    const successState = modal.querySelector('[data-modal-success]');
    modal.classList.remove('has-success');
    if (status) {
      status.textContent = '';
      status.style.color = '';
    }
    if (successState) {
      successState.setAttribute('hidden', '');
    }
    if (form) {
      form.reset();
      form.removeAttribute('hidden');
      form.querySelectorAll('[data-error-for]').forEach((el) => {
        el.textContent = '';
      });
      const submitButton = form.querySelector('.modal-form__submit');
      if (submitButton) {
        submitButton.removeAttribute('disabled');
        submitButton.classList.remove('is-disabled');
      }
    }
  }

  openButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const target = button.getAttribute('data-modal-open');
      const modal = document.querySelector(`[data-modal=\"${target}\"]`);
      if (!modal) return;

      modal.classList.add('is-open');
      modal.removeAttribute('hidden');
      body.classList.add('has-modal');
      const focusTarget = modal.querySelector('input, textarea, select, button');
      if (focusTarget) {
        focusTarget.focus();
      }
    });
  });

  initPropertyHeroGallery();
  initPropertyHeroSeasons();
  initPropertyGallery();
  initDateSearchForms();

  modals.forEach((modal) => {
    const overlay = modal.querySelector('.modal__overlay');
    let autoCloseTimer = null;

    const clearAutoClose = () => {
      if (autoCloseTimer) {
        window.clearTimeout(autoCloseTimer);
        autoCloseTimer = null;
      }
    };

    modal.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-modal-close]');
      if (closeTrigger || (overlay && event.target === overlay)) {
        clearAutoClose();
        closeModal(modal);
      }
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        clearAutoClose();
        closeModal(modal);
      }
    });

    const form = modal.querySelector('[data-modal-form]');
    const status = modal.querySelector('[data-modal-status]');
    const successState = modal.querySelector('[data-modal-success]');
    const successMessageEl = successState?.querySelector('[data-modal-success-message]') || successState;
    const submitButton = form?.querySelector('.modal-form__submit');

    const updateStatus = (message = '', color = '#d97957') => {
      if (!status) return;
      status.textContent = message;
      status.style.color = color;
    };

    if (form && status) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        updateStatus('', '#003947');
        const formData = new FormData(form);
        const errors = new Map();

        form.querySelectorAll('[data-error-for]').forEach((el) => {
          el.textContent = '';
        });

        const name = formData.get('name')?.toString().trim() ?? '';
        if (name === '') {
          errors.set('name', 'Please enter your full name.');
        }

        const guestsRaw = formData.get('guests')?.toString().trim() ?? '';
        const guestsValue = Number.parseInt(guestsRaw, 10);
        if (guestsRaw === '' || Number.isNaN(guestsValue)) {
          errors.set('guests', 'Please tell us how many guests are travelling.');
        } else if (guestsValue < 1) {
          errors.set('guests', 'Number of guests must be at least 1.');
        } else if (guestsValue > 24) {
          errors.set('guests', 'For groups over 24 guests, please contact us directly.');
        }

        const email = formData.get('email')?.toString().trim() ?? '';
        const emailConfirm = formData.get('email_confirm')?.toString().trim() ?? '';

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(email)) {
          errors.set('email', 'Please enter a valid email address.');
        }
        if (email !== emailConfirm) {
          errors.set('email_confirm', 'Email addresses do not match.');
        }

        const topic = formData.get('topic')?.toString() ?? '';
        if (topic === '') {
          errors.set('topic', 'Please choose an enquiry topic.');
        }

        const message = formData.get('message')?.toString().trim() ?? '';
        if (message.length < 50) {
          errors.set('message', 'Message must be at least 50 characters.');
        } else if (message.length > 3000) {
          errors.set('message', 'Message cannot exceed 3000 characters.');
        }

        if (errors.size > 0) {
          errors.forEach((value, key) => {
            const errorEl = modal.querySelector(`[data-error-for="${key}"]`);
            if (errorEl) {
              errorEl.textContent = value;
            }
          });
          updateStatus('Please fix the errors above.');
          return;
        }

        const payload = {
          name,
          guests: guestsValue,
          email,
          email_confirm: emailConfirm,
          topic,
          message,
        };

        try {
          if (submitButton) {
            submitButton.setAttribute('disabled', 'disabled');
            submitButton.classList.add('is-disabled');
          }

          const response = await fetch('/api/enquiry-modal.php', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(payload),
          });

          const result = await response.json();

          if (!response.ok) {
            if (response.status === 422 && result.errors) {
              Object.entries(result.errors).forEach(([key, value]) => {
                const errorEl = modal.querySelector(`[data-error-for="${key}"]`);
                if (errorEl) {
                  errorEl.textContent = value;
                }
              });
              updateStatus('Please fix the errors above.');
            } else {
              updateStatus(result.error || 'Something went wrong. Please try again.');
            }
            return;
          }

          if (successState) {
            successState.removeAttribute('hidden');
          }
          if (successMessageEl) {
            successMessageEl.textContent = 'Thank you! We\'ve emailed a confirmation and will reply shortly.';
          }
          updateStatus('');
          modal.classList.add('has-success');
          form.setAttribute('hidden', '');
          form.reset();
          clearAutoClose();
          autoCloseTimer = window.setTimeout(() => {
            closeModal(modal);
            autoCloseTimer = null;
          }, 3000);
        } catch (error) {
          updateStatus('Unable to send your enquiry right now. Please try again soon.');
        } finally {
          if (submitButton) {
            submitButton.removeAttribute('disabled');
            submitButton.classList.remove('is-disabled');
          }
        }
      });
    }
  });

  document.querySelectorAll('[data-catalog]').forEach((catalog) => {
    const buttons = Array.from(catalog.querySelectorAll('[data-catalog-filter]'));
    const cards = Array.from(catalog.querySelectorAll('[data-catalog-card]'));
    const emptyState = catalog.querySelector('[data-catalog-empty]');

    if (!buttons.length || !cards.length) {
      return;
    }

    const hashMap = new Map();
    buttons.forEach((button) => {
      const filterKey = button.dataset.catalogFilter ?? 'all';
      const hash = button.dataset.catalogHash ?? `#${filterKey}`;
      hashMap.set(hash, filterKey);
    });

    const applyFilter = (filterKey, updateHash = true) => {
      let visibleCount = 0;

      cards.forEach((card) => {
        const matches = filterKey === 'all' || card.dataset.category === filterKey;
        card.classList.toggle('is-hidden', !matches);
        if (matches) {
          visibleCount += 1;
        }
      });

      if (emptyState) {
        if (visibleCount === 0) {
          emptyState.removeAttribute('hidden');
        } else {
          emptyState.setAttribute('hidden', '');
        }
      }

      buttons.forEach((button) => {
        const isActive = (button.dataset.catalogFilter ?? 'all') === filterKey;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (updateHash) {
        const activeButton = buttons.find((button) => (button.dataset.catalogFilter ?? 'all') === filterKey);
        const hash = activeButton?.dataset.catalogHash ?? (filterKey === 'all' ? '#tab-all' : `#${filterKey}`);
        window.history.replaceState(null, '', hash);
      }
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const filterKey = button.dataset.catalogFilter ?? 'all';
        applyFilter(filterKey);
      });
    });

    const initialHash = window.location.hash;
    const initialFilter = hashMap.get(initialHash) ?? 'all';
    applyFilter(initialFilter, false);
  });

  document.querySelectorAll('[data-blog]').forEach((blog) => {
    const cards = Array.from(blog.querySelectorAll('[data-blog-card]'));
    const filterButtons = Array.from(blog.querySelectorAll('[data-blog-filter]'));
    const clearButtons = Array.from(blog.querySelectorAll('[data-blog-clear]'));
    const emptyState = blog.querySelector('[data-blog-empty]');

    if (!cards.length) {
      return;
    }

    const activeCategories = new Set();
    const activeTags = new Set();

    const applyFilters = () => {
      let visibleCount = 0;
      cards.forEach((card) => {
        const cardCategories = (card.dataset.categories || '').split(' ').filter(Boolean);
        const cardTags = (card.dataset.tags || '').split(' ').filter(Boolean);

        const categoriesMatch = activeCategories.size === 0 || Array.from(activeCategories).every((slug) => cardCategories.includes(slug));
        const tagsMatch = activeTags.size === 0 || Array.from(activeTags).every((slug) => cardTags.includes(slug));

        const isVisible = categoriesMatch && tagsMatch;
        card.classList.toggle('is-hidden', !isVisible);
        if (isVisible) {
          visibleCount += 1;
        }
      });

      if (emptyState) {
        if (visibleCount === 0) {
          emptyState.removeAttribute('hidden');
        } else {
          emptyState.setAttribute('hidden', '');
        }
      }
    };

    const toggleFilter = (collection, slug, button) => {
      if (collection.has(slug)) {
        collection.delete(slug);
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      } else {
        collection.add(slug);
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');
      }
      applyFilters();
    };

    filterButtons.forEach((button) => {
      const slug = button.dataset.blogFilterSlug;
      const type = button.dataset.blogFilterType;
      if (!slug || !type) {
        return;
      }

      button.addEventListener('click', () => {
        if (type === 'category') {
          toggleFilter(activeCategories, slug, button);
        } else if (type === 'tag') {
          toggleFilter(activeTags, slug, button);
        }
      });
    });

    clearButtons.forEach((button) => {
      const type = button.dataset.blogClear;
      button.addEventListener('click', () => {
        if (type === 'category') {
          activeCategories.clear();
          filterButtons.forEach((btn) => {
            if (btn.dataset.blogFilterType === 'category') {
              btn.classList.remove('is-active');
              btn.setAttribute('aria-pressed', 'false');
            }
          });
        } else if (type === 'tag') {
          activeTags.clear();
          filterButtons.forEach((btn) => {
            if (btn.dataset.blogFilterType === 'tag') {
              btn.classList.remove('is-active');
              btn.setAttribute('aria-pressed', 'false');
            }
          });
        }
        applyFilters();
      });
    });

    applyFilters();
  });

  document.querySelectorAll('[data-contact-form]').forEach((form) => {
    const emailField = form.querySelector('[data-contact-email]');
    const confirmField = form.querySelector('[data-contact-email-confirm]');
    const submitButton = form.querySelector('[data-contact-submit]');

    const toggleError = (input, hasError, message) => {
      const fieldWrapper = input ? input.closest('.contact-field') : null;
      if (!fieldWrapper) return;

      fieldWrapper.classList.toggle('has-error', hasError);
      const errorTarget = fieldWrapper.querySelector('[data-contact-error]');
      if (errorTarget) {
        errorTarget.textContent = hasError ? message : '';
      } else if (hasError) {
        const errorEl = document.createElement('p');
        errorEl.className = 'contact-field__error';
        errorEl.setAttribute('data-contact-error', input.name);
        errorEl.textContent = message;
        fieldWrapper.appendChild(errorEl);
      }
    };

    const clearError = (input) => {
      const fieldWrapper = input ? input.closest('.contact-field') : null;
      if (!fieldWrapper) return;
      fieldWrapper.classList.remove('has-error');
      const errorTarget = fieldWrapper.querySelector('[data-contact-error]');
      if (errorTarget) {
        errorTarget.textContent = '';
      }
    };

    if (emailField) {
      emailField.addEventListener('input', () => clearError(emailField));
    }

    if (confirmField) {
      confirmField.addEventListener('input', () => clearError(confirmField));
    }

    form.addEventListener('submit', (event) => {
      let hasError = false;
      const email = emailField ? emailField.value.trim() : '';
      const confirm = confirmField ? confirmField.value.trim() : '';

      if (email !== '' && confirm !== '' && email.toLowerCase() !== confirm.toLowerCase()) {
        hasError = true;
        toggleError(confirmField, true, 'Email addresses must match.');
        confirmField?.focus();
      }

      if (hasError) {
        event.preventDefault();
        return;
      }

      if (submitButton) {
        submitButton.setAttribute('disabled', 'disabled');
        submitButton.classList.add('is-disabled');
      }
    });
  });
});
